import { useState, useMemo, useEffect } from 'react';

import * as fp from 'lodash/fp';

import { CartModifier, CartOption } from '~/types/cart';
import { ModifierWithId } from '~/types/store/modifier';

export default function useCartItem(
  modifiers: Array<ModifierWithId>,
  defaultCartItem: {
    count: number;
    cartModifiers: Array<CartModifier>;
    note: string;
  } = { count: 1, cartModifiers: [], note: '' },
) {
  const [count, setCount] = useState<number>(defaultCartItem.count);
  const [note, setNote] = useState(defaultCartItem.note);
  const [cartModifiersMap, setCartModifiersMap] = useState<Map<string, CartModifier>>(
    defaultCartItem.cartModifiers.reduce((acc, modifier) => {
      acc.set(modifier.id, modifier);
      return acc;
    }, new Map()),
  );

  const [modifiersErrors, setModifiersErrors] = useState<Set<string>>(new Set());

  // update error
  useEffect(() => {
    setModifiersErrors(
      modifiers.reduce<Set<string>>((acc, modifier) => {
        const options = cartModifiersMap.get(modifier.id)?.options || [];

        const { min, max, isRequired } = modifier;
        const requireMin = isRequired ? (fp.eq(min, 0) ? min + 1 : min) : 0;

        (fp.gt(options.length, max) || fp.lt(options.length, requireMin)) && acc.add(modifier.id);

        return acc;
      }, new Set()),
    );
  }, [modifiers, cartModifiersMap]);

  const handleChangeCartModifiers = (modifiers: Map<string, CartModifier>) => {
    setCartModifiersMap(modifiers);
  };

  const handleChangeNote = (note: string | ((prev: string) => string)) => {
    setNote(note);
  };

  const handleChangeCount = (count: number | ((prev: number) => number)) => {
    setCount(count);
  };

  const optionsTotalPrice = useMemo(() => {
    return fp.pipe<[Array<CartModifier>], Array<CartOption>, number>(
      fp.flatMap(({ options }) => options),
      fp.sumBy(({ price }) => price),
    )([...cartModifiersMap.values()]);
  }, [cartModifiersMap]);

  return {
    count,
    note,
    cartModifiersMap,
    modifiersErrors,
    optionsTotalPrice,
    handleChangeCartModifiers,
    handleChangeCount,
    handleChangeNote,
  };
}
